/**
 * Bundled FHIR-aligned sample ENCOUNTERS (EncounterView) — demo fallback.
 *
 * Used by RecentEncounters when the deployed site cannot reach the clinical
 * API (or the patient simply has no rows yet): the section renders these
 * contract-exact samples with a "Sample data" badge instead of an empty or
 * error panel, so the demo stays fully clickable on pages.dev.
 *
 * Shapes mirror Dex's deployed `_encounter_to_view` EXACTLY
 * (docs/design/provider-backend-api-2026-08-30.md §6.1, commits 93462a4 /
 * aa7df5c): `resource_type: 'Encounter'`, `type` carries the FHIR class code,
 * ISO period, ICD-10 reason code + text, practitioner id reference, ward /
 * clinic location, discharge disposition, and embedded notes with
 * id / note_type / source / author / taken_at / title / body / signed_status /
 * addendum_of.
 *
 * ids are stable UUIDs — detail lookups match them verbatim.
 */
import type { EncounterView } from '../../api/clinical'

const PATIENT_ID = '7f381bff-5fa9-4fee-92a0-f8e21a163e19'

export const SAMPLE_ENCOUNTERS: EncounterView[] = [
  {
    id: '5a000001-0000-4000-8000-000000000001',
    resource_type: 'Encounter',
    patient_id: PATIENT_ID,
    facility_id: 'AL-NOUR-DMC',
    status: 'finished',
    type: 'outpatient',
    period_start: '2026-08-18T09:30:00+00:00',
    period_end: '2026-08-18T10:05:00+00:00',
    reason_code: 'E11.9',
    reason_text: 'Routine diabetes follow-up',
    practitioner_id: '3c000001-0000-4000-8000-0000000000c1',
    practitioner_name: 'Dr. Layal Haddad',
    location_ward: 'Outpatient Clinics',
    location_clinic: 'Endocrinology Clinic A',
    discharge_disposition: null,
    created_at: '2026-08-18T10:06:00+00:00',
    updated_at: '2026-08-18T10:06:00+00:00',
    notes: [
      {
        id: '6a000001-0000-4000-8000-000000000001',
        note_type: 'progress',
        source: 'clinician',
        author: 'Dr. Layal Haddad',
        taken_at: '2026-08-18T10:05:00+00:00',
        title: 'T2DM routine follow-up',
        body: 'HbA1c 7.4% (down from 7.9% in May). Fasting glucose 128 mg/dL. Continue metformin 500 mg BID; reinforce carb counting. Foot exam normal, monofilament intact. Next review in 3 months with repeat HbA1c and lipid panel.',
        signed_status: 'signed',
        addendum_of: null,
      },
      {
        id: '6a000002-0000-4000-8000-000000000002',
        note_type: 'phone',
        source: 'patient',
        author: 'Rana Khalil, RN',
        taken_at: '2026-08-19T11:20:00+00:00',
        title: 'Patient call — hypoglycemia query',
        body: 'Patient reports one episode of shakiness before lunch, resolved with juice, glucometer read 74 mg/dL. No loss of consciousness. Advised on snack timing and to log readings for one week.',
        signed_status: 'signed',
        addendum_of: null,
      },
    ],
  },
  {
    id: '5a000002-0000-4000-8000-000000000002',
    resource_type: 'Encounter',
    patient_id: PATIENT_ID,
    facility_id: 'AL-NOUR-DMC',
    status: 'finished',
    type: 'outpatient',
    period_start: '2026-08-05T13:15:00+00:00',
    period_end: '2026-08-05T13:45:00+00:00',
    reason_code: 'I10',
    reason_text: 'Hypertension review',
    practitioner_id: '3c000002-0000-4000-8000-0000000000c2',
    practitioner_name: 'Dr. Sami Awad',
    location_ward: 'Outpatient Clinics',
    location_clinic: 'Family Medicine Clinic 2',
    discharge_disposition: null,
    created_at: '2026-08-05T13:47:00+00:00',
    updated_at: '2026-08-05T13:47:00+00:00',
    notes: [
      {
        id: '6a000003-0000-4000-8000-000000000003',
        note_type: 'progress',
        source: 'clinician',
        author: 'Dr. Sami Awad',
        taken_at: '2026-08-05T13:42:00+00:00',
        title: 'HTN medication titration',
        body: 'BP 138/86 today (home average 142/90). Adherence good per pill count. Amlodipine increased 5 → 10 mg OD. Counselled on sodium < 2 g/day and 30 min brisk walking. Recheck BP in 4 weeks; labs (K+, creatinine) ordered.',
        signed_status: 'signed',
        addendum_of: null,
      },
    ],
  },
  {
    id: '5a000003-0000-4000-8000-000000000003',
    resource_type: 'Encounter',
    patient_id: PATIENT_ID,
    facility_id: 'AL-NOUR-DMC',
    status: 'finished',
    type: 'telehealth',
    period_start: '2026-07-22T16:00:00+00:00',
    period_end: '2026-07-22T16:20:00+00:00',
    reason_code: 'Z01.8',
    reason_text: 'Laboratory results review',
    practitioner_id: '3c000001-0000-4000-8000-0000000000c1',
    practitioner_name: 'Dr. Layal Haddad',
    location_ward: null,
    location_clinic: 'Telehealth — Diabetes Program',
    discharge_disposition: null,
    created_at: '2026-07-22T16:21:00+00:00',
    updated_at: '2026-07-22T16:21:00+00:00',
    notes: [
      {
        id: '6a000004-0000-4000-8000-000000000004',
        note_type: 'phone',
        source: 'clinician',
        author: 'Dr. Layal Haddad',
        taken_at: '2026-07-22T16:18:00+00:00',
        title: 'Lab review call',
        body: 'Reviewed lipid panel: LDL 2.9 mmol/L (target < 2.6), ALT 22 U/L normal, eGFR 92. Started adherence discussion re: statin — patient prefers diet trial first; agreed 3-month diet/exercise window then reassess.',
        signed_status: 'signed',
        addendum_of: null,
      },
    ],
  },
  {
    id: '5a000004-0000-4000-8000-000000000004',
    resource_type: 'Encounter',
    patient_id: PATIENT_ID,
    facility_id: 'AL-NOUR-DMC',
    status: 'finished',
    type: 'emergency',
    period_start: '2026-06-30T22:10:00+00:00',
    period_end: '2026-07-01T01:35:00+00:00',
    reason_code: 'J06.9',
    reason_text: 'Acute upper respiratory infection',
    practitioner_id: '3c000003-0000-4000-8000-0000000000c3',
    practitioner_name: 'Dr. Hana Barakat',
    location_ward: 'Emergency Department',
    location_clinic: 'Al-Nour ER — Bay 4',
    discharge_disposition: 'home',
    created_at: '2026-07-01T01:40:00+00:00',
    updated_at: '2026-07-01T01:40:00+00:00',
    notes: [
      {
        id: '6a000005-0000-4000-8000-000000000005',
        note_type: 'progress',
        source: 'clinician',
        author: 'Dr. Hana Barakat',
        taken_at: '2026-07-01T00:15:00+00:00',
        title: 'ED assessment — URI',
        body: '3 days of sore throat, low-grade fever 38.1°C, dry cough. Throat erythematous, no exudate; chest clear bilaterally; SpO2 99% RA. Viral picture — supportive care only, no antibiotics. Glucose 9.8 mmol/L on arrival, rechecked 8.4 mmol/L — consistent with illness stress response.',
        signed_status: 'signed',
        addendum_of: null,
      },
      {
        id: '6a000006-0000-4000-8000-000000000006',
        note_type: 'discharge-summary',
        source: 'clinician',
        author: 'Dr. Hana Barakat',
        taken_at: '2026-07-01T01:30:00+00:00',
        title: 'Discharge instructions',
        body: 'الخروج إلى المنزل مع الراحة وشرب السوائل، ومراجعة أقرب منشأة صحية إذا تجاوزت الحرارة ٣٩ درجة أو ظهرت صعوبة في التنفس. متابعة السكري مع العيادة خلال أسبوع.',
        signed_status: 'signed',
        addendum_of: null,
      },
    ],
  },
  {
    id: '5a000005-0000-4000-8000-000000000005',
    resource_type: 'Encounter',
    patient_id: PATIENT_ID,
    facility_id: 'AL-NOUR-DMC',
    status: 'finished',
    type: 'outpatient',
    period_start: '2026-05-12T10:00:00+00:00',
    period_end: '2026-05-12T10:40:00+00:00',
    reason_code: 'E78.5',
    reason_text: 'Dyslipidemia — annual review',
    practitioner_id: '3c000001-0000-4000-8000-0000000000c1',
    practitioner_name: 'Dr. Layal Haddad',
    location_ward: 'Outpatient Clinics',
    location_clinic: 'Endocrinology Clinic A',
    discharge_disposition: null,
    created_at: '2026-05-12T10:41:00+00:00',
    updated_at: '2026-05-12T10:41:00+00:00',
    notes: [
      {
        id: '6a000007-0000-4000-8000-000000000007',
        note_type: 'progress',
        source: 'clinician',
        author: 'Dr. Layal Haddad',
        taken_at: '2026-05-12T10:35:00+00:00',
        title: 'Annual metabolic review',
        body: 'Annual review: weight 82 kg (stable), BMI 29.1. HbA1c 7.9% — metformin restarted after self-discontinuation. Retinal screening booked (last: 2025-04, no retinopathy). Influenza + COVID boosters offered.',
        signed_status: 'amended',
        addendum_of: null,
      },
    ],
  },
]
